import { Badge, IcnBell, IconButton, Popover } from '@viquard/doc-ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NOTIFICATIONS_REFETCH_INTERVAL_MS,
  useNotifications,
} from '@/features/notifications/api';
import { formatBadgeCount } from '@/features/notifications/format';
import { NotificationsPanel } from '@/features/notifications/views';

export interface NotificationsTriggerProps {
  unread?: number;
  onOpen?: () => void;
}

export function NotificationsTrigger({ unread, onOpen }: NotificationsTriggerProps): ReactNode {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const count = useNotifications({
    limit: 1,
    enabled: unread === undefined,
    refetchInterval: NOTIFICATIONS_REFETCH_INTERVAL_MS,
  });
  const unreadCount = unread ?? count.data?.unreadCount ?? 0;

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (next) onOpen?.();
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottom-end"
        padless
        aria-label={t('notifications.label')}
        content={({ close }) => <NotificationsPanel onClose={close} />}
      >
        <IconButton
          size="sm"
          variant="ghost"
          label={t('notifications.label')}
          active={open}
          toggle
        >
          <IcnBell size={16} />
        </IconButton>
      </Popover>
      {unreadCount > 0 ? (
        <Badge
          tone="danger"
          variant="solid"
          size="sm"
          numeric
          label={t('notifications.unreadLabel', { count: unreadCount })}
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            minWidth: 18,
            height: 18,
            paddingInline: 5,
            boxShadow: '0 0 0 2px var(--vq-paper-raised)',
            pointerEvents: 'none',
          }}
        >
          {formatBadgeCount(unreadCount)}
        </Badge>
      ) : null}
    </span>
  );
}
